import { AUTO_DISMISS_MS } from "@/constants/common";
import { useTranslation } from "react-i18next";
import type { AlertEntry, AlertEvent, AlertInput, AlertManager, AlertSubscriber } from "@/types/common";
import { useCallback } from "react";

export function createAlertManager(): AlertManager {
  const subscribers = new Set<AlertSubscriber>();
  const alerts: AlertEntry[] = [];

  const notify = (event: AlertEvent) => {
    subscribers.forEach((subscriber) => subscriber(event));
  };

  const dismiss = (id: string) => {
    const index = alerts.findIndex((alert) => alert.id === id);
    if (index >= 0) {
      alerts.splice(index, 1);
    }
    notify({ id, dismiss: true });
  };

  const addAlert = (entry: AlertInput): string => {
    const id = entry.id ?? `alert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const next: AlertEntry = { ...entry, id };
    alerts.push(next);
    notify(next);

    setTimeout(() => {
      dismiss(id);
    }, AUTO_DISMISS_MS);

    return id;
  };

  return {
    subscribe: (subscriber: AlertSubscriber) => {
      subscribers.add(subscriber);
      return () => subscribers.delete(subscriber);
    },
    addAlert,
    dismiss,
    dismissAll: () => {
      const ids = alerts.map((alert) => alert.id).filter(Boolean) as string[];
      alerts.length = 0;
      ids.forEach((id) => notify({ id, dismiss: true }));
    },
    getAlerts: () => [...alerts],
  };
}

export const globalAlertManager = createAlertManager();

export function useAlertNotifications() {
  const { t } = useTranslation();

  const showSuccessAlert = useCallback((message: string) => {
    globalAlertManager.addAlert({
      type: "success",
      title: t("alerts.successTitle"),
      description: message,
    });
  }, [t]);

  const showErrorAlert = useCallback((message: string) => {
    const alertEntry = {
      type: "error" as const,
      title: t("alerts.errorTitle"),
      description: message,
    };
    globalAlertManager.addAlert(alertEntry);
  }, [t]);

  return { showSuccessAlert, showErrorAlert };
}
